import React, { useEffect, useRef, useState } from "react";
import { Button, Input } from "antd";
import { ArrowLeft, Phone, RefreshCw, CheckCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const PhoneVerificationPage = ({ phone: phoneProp = "", onVerificationSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // prefer state.phone (passed from sign up or email verification), fallback to prop
  const phoneFromState = location.state?.phone || phoneProp || "";

  const [otpArr, setOtpArr] = useState(Array(6).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const inputsRef = useRef([]);
  const resendIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
    };
  }, []);

  const otp = otpArr.join("");

  const handleChange = (value, idx) => {
    const char = value.slice(-1).replace(/\s+/g, "");
    if (char && !/^[0-9]$/.test(char)) return; // only digits

    const next = [...otpArr];
    next[idx] = char;
    setOtpArr(next);

    if (char && idx < 5) {
      const nextInput = inputsRef.current[idx + 1];
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otpArr[idx] === "") {
        if (idx > 0) {
          const prevInput = inputsRef.current[idx - 1];
          if (prevInput) prevInput.focus();
          const next = [...otpArr];
          next[idx - 1] = "";
          setOtpArr(next);
        }
      } else {
        const next = [...otpArr];
        next[idx] = "";
        setOtpArr(next);
      }
    }

    if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowRight" && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const digits = paste.replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length === 0) return;
    const next = [...otpArr];
    for (let i = 0; i < 6; i++) {
      next[i] = digits[i] || "";
    }
    setOtpArr(next);
    const firstEmpty = next.findIndex((d) => d === "");
    const focusIndex = firstEmpty === -1 ? 5 : firstEmpty;
    setTimeout(() => inputsRef.current[focusIndex]?.focus(), 0);
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;

    setIsVerifying(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsVerifying(false);
    onVerificationSuccess && onVerificationSuccess();
    navigate("/login", { replace: true });
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendTimer(60);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsResending(false);

    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }
    resendIntervalRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current);
          resendIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const maskedPhone = (phoneFromState || "").replace(/(\d{3})(\d{3})(\d{4})/, "$1-***-$3");

  return (
    <div className="relative z-10 flex min-h-[100dvh] items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Button
          ghost
          onClick={() => navigate("/signup", { replace: true })}
          className="mb-8 text-gray-400 hover:text-white hover:bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sign Up
        </Button>

        {/* Main Card */}
        <div className="bg-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
          {/* Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-emerald-500/5 rounded-2xl" />
          <div className="absolute -top-1 -left-1 -right-1 -bottom-1 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-green-500/20 rounded-2xl blur-sm -z-10" />

          <div className="relative">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/25">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-white mb-2">Verify Your Phone</h1>
              <p className="text-gray-400">We've sent a 6-digit verification code to</p>
              <p className="text-green-400 mt-1">{maskedPhone}</p>
            </div>

            {/* Progress Indicator */}
            <div className="flex items-center justify-center mb-6 space-x-2">
              <div className="flex items-center text-green-400">
                <CheckCircle className="w-4 h-4 mr-1" />
                <span className="text-sm">Email Verified</span>
              </div>
              <div className="w-8 h-px bg-slate-600"></div>
              <div className="flex items-center text-blue-400">
                <div className="w-4 h-4 rounded-full border-2 border-blue-400 mr-1"></div>
                <span className="text-sm">Phone Verification</span>
              </div>
            </div>

            {/* OTP Input */}
            <div className="mb-6">
              <label className="block text-gray-300 mb-4 text-center">Enter verification code</label>
              <div className="flex justify-center gap-3">
                {otpArr.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el?.input ?? el)}
                    value={digit}
                    onChange={(e) => handleChange(e.target.value, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    onPaste={handlePaste}
                    maxLength={1}
                    size="large"
                    inputMode="numeric"
                    className="w-12 h-12 text-center text-white text-lg rounded-lg bg-slate-800/50 border-2 border-slate-600 focus:border-green-500 focus:bg-slate-700/50 transition-all duration-200"
                  />
                ))}
              </div>
            </div>

            {/* Verify Button */}
            <Button
              onClick={handleVerifyOTP}
              disabled={otp.length !== 6 || isVerifying}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 h-12 rounded-lg shadow-lg shadow-green-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isVerifying ? (
                <span className="inline-flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </span>
              ) : (
                "Complete Verification"
              )}
            </Button>

            {/* Resend Code */}
            <div className="mt-6 text-center">
              <p className="text-gray-400 mb-2">Didn't receive the code?</p>
              <Button
                ghost
                onClick={handleResendCode}
                disabled={isResending || resendTimer > 0}
                className="text-green-400 hover:text-green-300 hover:bg-slate-700/50 disabled:!text-white"
              >
                {isResending ? (
                  <span className="inline-flex items-center text-white">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </span>
                ) : resendTimer > 0 ? (
                  `Resend in ${resendTimer}s`
                ) : (
                  "Resend Code"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationPage;
